import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem, MaskLines } from "@/components/Reveal";
import { Section, Eyebrow } from "@/components/Bits";
import ArrowLink from "@/components/ArrowLink";
import AppCard from "@/components/AppCard";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import Marquee from "@/components/Marquee";
import { APPS, PRINCIPLES, RESOURCE_CATEGORIES, ARTICLES, IMAGES, logo } from "@/data/site";

const PCOLOR = {
  purple: "bg-purple text-cream",
  turquoise: "bg-turquoise text-charcoal",
  marigold: "bg-marigold text-cream",
  sunny: "bg-sunny text-charcoal",
};

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 12]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-cream px-5 pb-16 pt-28 md:px-8 md:pt-36">
      {/* decorative color blocks */}
      <motion.div style={{ y: yImg }} className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-turquoise/25 blur-2xl" />
      <div className="pointer-events-none absolute -left-16 bottom-10 h-56 w-56 rounded-full bg-sunny/30 blur-2xl" />

      <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-charcoal/15 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-purple">
              <Sparkles className="h-3.5 w-3.5 text-marigold" /> Home education, done differently
            </span>
          </motion.div>

          <h1 className="mt-6 font-display text-[2.7rem] font-extrabold leading-[0.95] tracking-tight text-charcoal sm:text-6xl lg:text-[5rem]">
            <MaskLines
              lines={["Learning doesn't", "have to look"]}
              lineClassName=""
            />
            <span className="line-mask">
              <motion.span
                className="block text-purple"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.39 }}
              >
                like school.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-charcoal/75 sm:text-xl"
          >
            Practical tools and resources built for home education — by people who actually use them.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <ArrowLink to="/apps" variant="primary" testid="hero-explore-apps">Explore our apps</ArrowLink>
            <ArrowLink to="/about" variant="outline" arrow={false} testid="hero-why-freerange">Why FreeRange?</ArrowLink>
          </motion.div>
        </div>

        {/* Logo composition + hero visual */}
        <div className="relative">
          <motion.div style={{ y: yImg }} className="group relative">
            <ImagePlaceholder
              src={IMAGES.heroLifestyle}
              alt="A family learning together at home"
              ratio="hero"
              rounded="rounded-[2rem]"
              testid="hero-image"
              className="border-4 border-white shadow-solid-purple"
            />
          </motion.div>
          <motion.div
            style={{ y: yLogo, rotate }}
            className="absolute -bottom-8 -left-6 flex h-28 w-28 items-center justify-center rounded-full border-4 border-white bg-cream shadow-ambient sm:h-36 sm:w-36"
          >
            <img src={logo} alt="FreeRange.Education" className="h-full w-full rounded-full" />
          </motion.div>
          <div className="absolute -right-3 top-6 rounded-2xl bg-sunny px-4 py-2 font-display text-sm font-bold text-charcoal shadow-solid rotate-3">
            Built for families
          </div>
        </div>
      </div>
    </section>
  );
}

function Intro() {
  return (
    <Section className="py-14 md:py-20">
      <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <Reveal>
          <Eyebrow>Our approach</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            Built for the way home education actually works.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="max-w-xl lg:pt-8">
          <p className="text-lg leading-relaxed text-charcoal/75">
            FreeRange.Education creates practical digital tools and resources for home-educating families.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-charcoal/75">
            Built from first-hand experience, our products are shaped around the tools we wanted to use ourselves —
            flexible, useful and designed to fit around the way families actually learn.
          </p>
        </Reveal>
      </div>
    </Section>
  );
}

function Principles() {
  return (
    <Section className="pb-14 md:pb-20">
      <Reveal className="mb-10">
        <Eyebrow color="text-turquoise">What we believe</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl">
          Four ideas behind everything we build.
        </h2>
      </Reveal>
      <Stagger className="grid gap-5 md:grid-cols-2">
        {PRINCIPLES.map((p, i) => (
          <StaggerItem key={p.tag}>
            <div
              data-testid={`principle-${i}`}
              className={`flex h-full flex-col rounded-3xl p-8 md:p-10 ${PCOLOR[p.color]} transition-transform duration-300 hover:-translate-y-1`}
            >
              <span className="font-display text-5xl font-extrabold opacity-30">0{i + 1}</span>
              <span className="mt-6 text-xs font-bold uppercase tracking-[0.18em] opacity-80">{p.tag}</span>
              <h3 className="mt-2 font-display text-2xl font-bold leading-snug tracking-tight">{p.title}</h3>
              <p className="mt-3 leading-relaxed opacity-90">{p.body}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </Section>
  );
}

function AppsSection() {
  return (
    <Section className="pt-4 pb-14 md:pt-6 md:pb-20">
      <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>In the workshop</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-5xl">
            Tools we're building
          </h2>
          <p className="mt-4 max-w-lg text-lg leading-relaxed text-charcoal/70">
            Digital tools designed specifically around home education.
          </p>
        </div>
        <ArrowLink to="/apps" variant="outline" testid="apps-see-all">See all apps</ArrowLink>
      </Reveal>
      <Stagger className="grid gap-6 lg:grid-cols-2">
        {APPS.map((app) => (
          <StaggerItem key={app.slug}>
            <AppCard app={app} />
          </StaggerItem>
        ))}
      </Stagger>
    </Section>
  );
}

function ResourcesSection() {
  return (
    <Section className="pb-24 md:pb-32">
      <Reveal className="mb-12">
        <Eyebrow color="text-marigold">Practical resources</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-5xl">
          Practical resources for home education
        </h2>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-charcoal/70">
          Useful downloads, templates and materials designed around real home-education needs.
        </p>
      </Reveal>
      <Stagger className="grid gap-6 md:grid-cols-3">
        {RESOURCE_CATEGORIES.map((c) => (
          <StaggerItem key={c.slug}>
            <div className="group flex h-full flex-col overflow-hidden rounded-3xl border border-charcoal/10 bg-white transition-transform duration-300 hover:-translate-y-1.5">
              <ImagePlaceholder src={c.image} alt={c.title} ratio="wide" rounded="rounded-none" testid={`resource-img-${c.slug}`} />
              <div className="flex flex-1 flex-col p-7">
                <h3 className="font-display text-2xl font-bold tracking-tight text-charcoal">{c.title}</h3>
                <p className="mt-2 leading-relaxed text-charcoal/70">{c.body}</p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      <Reveal className="mt-10">
        <ArrowLink to="/resources" variant="primary" testid="explore-resources">Explore Resources</ArrowLink>
      </Reveal>
    </Section>
  );
}

function IdeasSection() {
  const featured = ARTICLES.find((a) => a.featured) || ARTICLES[0];
  const rest = ARTICLES.filter((a) => a.slug !== featured.slug).slice(0, 3);
  return (
    <Section className="py-24 md:py-32">
      <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow color="text-turquoise">From the notebook</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-5xl">Ideas & Guides</h2>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-charcoal/70">
            Thoughtful, practical articles about home education, learning and the tools we build.
          </p>
        </div>
        <ArrowLink to="/guides" variant="outline" testid="browse-guides">Browse Ideas & Guides</ArrowLink>
      </Reveal>

      <div className="grid gap-6 lg:grid-cols-2">
        <Reveal>
          <Link to={`/guides/${featured.slug}`} className="group block overflow-hidden rounded-3xl bg-purple text-cream" data-testid="featured-article">
            <div className="overflow-hidden">
              <ImagePlaceholder src={featured.image} alt={featured.title} ratio="wide" rounded="rounded-none" testid="featured-article-img" />
            </div>
            <div className="p-8">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-turquoise">Featured · {featured.category}</span>
              <h3 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight">{featured.title}</h3>
              <p className="mt-3 leading-relaxed text-cream/80">{featured.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-semibold text-turquoise">
                Read article <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </Reveal>

        <Stagger className="flex flex-col gap-4">
          {rest.map((a) => (
            <StaggerItem key={a.slug}>
              <Link
                to={`/guides/${a.slug}`}
                className="group flex items-center gap-5 rounded-2xl border border-charcoal/10 bg-white p-4 transition-transform duration-300 hover:-translate-y-1"
                data-testid={`article-preview-${a.slug}`}
              >
                <div className="w-28 shrink-0 overflow-hidden rounded-xl sm:w-40">
                  <ImagePlaceholder src={a.image} alt={a.title} ratio="square" rounded="rounded-xl" testid={`article-img-${a.slug}`} />
                </div>
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-marigold">{a.category}</span>
                  <h4 className="mt-1 font-display text-xl font-bold leading-snug tracking-tight text-charcoal">{a.title}</h4>
                  <span className="mt-1 block text-sm text-charcoal/50">{a.readTime}</span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </Section>
  );
}

export default function Home() {
  return (
    <>
      <Seo title="FreeRange.Education | Tools Built for Home Education" />
      <Hero />
      <Intro />
      <Principles />
      <AppsSection />
      <ResourcesSection />
      <Marquee text="Tools built for home education" />
      <IdeasSection />
    </>
  );
}
